import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { z } from "zod";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Layers3,
  MessageSquareHeart,
  Sparkles,
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { submitFeedback } from "@/lib/feedbackApi";
import { ROUTES } from "@/lib/routeConstants";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { StandardPageShell } from "@/components/layout/StandardPageShell";

const USAGE_FREQUENCIES = [
  "first-time",
  "occasionally",
  "weekly",
  "daily",
] as const;
const FEEDBACK_AREAS = [
  "learning-materials",
  "exam-simulation",
  "study-partner",
  "analysis-results",
] as const;
const DEVICE_CONTEXTS = ["desktop", "mobile", "tablet", "mixed"] as const;
const IMPROVEMENT_AREAS = [
  "navigation",
  "clarity",
  "performance",
  "content-quality",
  "bugs",
  "missing-features",
  "mobile-experience",
] as const;

type UsageFrequency = (typeof USAGE_FREQUENCIES)[number];
type FeedbackArea = (typeof FEEDBACK_AREAS)[number];
type DeviceContext = (typeof DEVICE_CONTEXTS)[number];
type ImprovementArea = (typeof IMPROVEMENT_AREAS)[number];

interface FeedbackFormValues {
  overallRating: number;
  usageFrequency: UsageFrequency;
  goalSupportRating: number;
  usedAreas: FeedbackArea[];
  primaryArea: FeedbackArea;
  deviceContext: DeviceContext;
  likedMost: string;
  improvementAreas: ImprovementArea[];
  experienceDetails: string;
  suggestions: string;
  contactConsent: boolean;
  contactEmail: string;
}

const createFeedbackFormSchema = (t: TFunction) =>
  z
    .object({
      overallRating: z
        .number({
          required_error: t("feedbackPage.validation.overallRating"),
          invalid_type_error: t("feedbackPage.validation.overallRating"),
        })
        .min(1, t("feedbackPage.validation.overallRating")),
      usageFrequency: z.enum(USAGE_FREQUENCIES, {
        required_error: t("feedbackPage.validation.usageFrequency"),
      }),
      goalSupportRating: z
        .number({
          required_error: t("feedbackPage.validation.goalSupportRating"),
          invalid_type_error: t("feedbackPage.validation.goalSupportRating"),
        })
        .min(1, t("feedbackPage.validation.goalSupportRating")),
      usedAreas: z
        .array(z.enum(FEEDBACK_AREAS))
        .min(1, t("feedbackPage.validation.usedAreas")),
      primaryArea: z.enum(FEEDBACK_AREAS, {
        required_error: t("feedbackPage.validation.primaryArea"),
      }),
      deviceContext: z.enum(DEVICE_CONTEXTS, {
        required_error: t("feedbackPage.validation.deviceContext"),
      }),
      likedMost: z
        .string()
        .trim()
        .min(20, t("feedbackPage.validation.likedMost")),
      improvementAreas: z.array(z.enum(IMPROVEMENT_AREAS)).default([]),
      experienceDetails: z
        .string()
        .trim()
        .min(20, t("feedbackPage.validation.experienceDetails")),
      suggestions: z
        .string()
        .trim()
        .min(20, t("feedbackPage.validation.suggestions")),
      contactConsent: z.boolean().default(false),
      contactEmail: z.string().trim().default(""),
    })
    .superRefine((values, context) => {
      if (!values.contactConsent) {
        return;
      }

      if (!values.contactEmail) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["contactEmail"],
          message: t("feedbackPage.validation.contactEmailRequired"),
        });
        return;
      }

      if (!z.string().email().safeParse(values.contactEmail).success) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["contactEmail"],
          message: t("feedbackPage.validation.contactEmailInvalid"),
        });
      }
    });

const STEP_ICONS = [
  Sparkles,
  Layers3,
  ClipboardList,
  MessageSquareHeart,
] as const;

export default function Feedback() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(createFeedbackFormSchema(t)),
    mode: "onTouched",
    defaultValues: {
      usedAreas: [],
      improvementAreas: [],
      likedMost: "",
      experienceDetails: "",
      suggestions: "",
      contactConsent: false,
      contactEmail: user?.email ?? "",
    },
  });

  const contactConsent = form.watch("contactConsent");

  const steps = [
    {
      id: "overall",
      title: t("feedbackPage.steps.overall.title"),
      description: t("feedbackPage.steps.overall.description"),
    },
    {
      id: "usage",
      title: t("feedbackPage.steps.usage.title"),
      description: t("feedbackPage.steps.usage.description"),
    },
    {
      id: "details",
      title: t("feedbackPage.steps.details.title"),
      description: t("feedbackPage.steps.details.description"),
    },
    {
      id: "suggestions",
      title: t("feedbackPage.steps.suggestions.title"),
      description: t("feedbackPage.steps.suggestions.description"),
    },
  ] as const;

  const stepFields: Array<Array<keyof FeedbackFormValues>> = [
    ["overallRating", "usageFrequency", "goalSupportRating"],
    ["usedAreas", "primaryArea", "deviceContext"],
    ["likedMost", "improvementAreas", "experienceDetails"],
    ["suggestions", "contactConsent", "contactEmail"],
  ];

  const progressValue = submitted
    ? 100
    : ((currentStep + 1) / steps.length) * 100;
  const CurrentIcon = STEP_ICONS[currentStep];

  const resetForm = () => {
    form.reset({
      usedAreas: [],
      improvementAreas: [],
      likedMost: "",
      experienceDetails: "",
      suggestions: "",
      contactConsent: false,
      contactEmail: user?.email ?? "",
    });
    setSubmitted(false);
    setCurrentStep(0);
  };

  const handleNext = async () => {
    const isValid = await form.trigger(stepFields[currentStep], {
      shouldFocus: true,
    });
    if (isValid) {
      setCurrentStep((step) => Math.min(step + 1, steps.length - 1));
    }
  };

  const handleBack = () => {
    setCurrentStep((step) => Math.max(step - 1, 0));
  };

  const handleSubmit = async (values: FeedbackFormValues) => {
    setIsSubmitting(true);
    const result = await submitFeedback({
      overallRating: values.overallRating,
      usageFrequency: values.usageFrequency,
      goalSupportRating: values.goalSupportRating,
      usedAreas: values.usedAreas,
      primaryArea: values.primaryArea,
      deviceContext: values.deviceContext,
      likedMost: values.likedMost,
      improvementAreas: values.improvementAreas,
      experienceDetails: values.experienceDetails,
      suggestions: values.suggestions,
      contactConsent: values.contactConsent,
      contactEmail: values.contactEmail ?? "",
    });
    setIsSubmitting(false);
    if (result) {
      setSubmitted(true);
      setCurrentStep(0);
    }
  };

  return (
    <StandardPageShell
      title={t("feedbackPage.title")}
      headerRight={<LanguageSwitcher />}
      className="mx-auto w-full max-w-7xl flex-1 px-3 py-4 sm:px-6 sm:py-8 lg:px-8"
    >
      <div>
        <div className="mb-6">
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground sm:text-base">
            {t("feedbackPage.subtitle")}
          </p>
        </div>

        <Card className="overflow-hidden rounded-2xl border-border/70 shadow-sm sm:rounded-[1.75rem]">
          <div className="border-b border-border/60 bg-muted/20 px-4 py-4 sm:px-8 sm:py-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex min-w-0 items-start gap-3 sm:gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary sm:h-12 sm:w-12 sm:rounded-2xl">
                  <CurrentIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">
                    {t("feedbackPage.progressLabel", {
                      current: submitted ? steps.length : currentStep + 1,
                      total: steps.length,
                    })}
                  </p>
                  <h2 className="mt-1 font-ui text-xl font-semibold text-foreground">
                    {submitted
                      ? t("feedbackPage.success.title")
                      : steps[currentStep].title}
                  </h2>
                  <p className="mt-1 max-w-xl text-sm text-muted-foreground">
                    {submitted
                      ? t("feedbackPage.success.description")
                      : steps[currentStep].description}
                  </p>
                </div>
              </div>
              <Badge
                variant="outline"
                className="w-fit border-border/70 bg-background/80 text-foreground"
              >
                {submitted
                  ? t("feedbackPage.success.badge")
                  : t("feedbackPage.progressLabel", {
                      current: currentStep + 1,
                      total: steps.length,
                    })}
              </Badge>
            </div>

            <Progress value={progressValue} className="mt-5 h-2 bg-muted" />

            <div className="scrollbar-hide mt-4 grid grid-flow-col auto-cols-[minmax(8rem,1fr)] gap-2 overflow-x-auto sm:mt-5 sm:grid-flow-row sm:grid-cols-4 sm:overflow-visible">
              {steps.map((step, index) => (
                <div
                  key={step.id}
                  className={cn(
                    "rounded-2xl border px-3 py-3 transition-colors",
                    submitted || index === currentStep
                      ? "border-primary/30 bg-primary/10 text-foreground"
                      : index < currentStep
                        ? "border-border bg-background text-foreground"
                        : "border-border/70 bg-background/70 text-muted-foreground",
                  )}
                >
                  <p className="text-xs font-medium uppercase tracking-[0.16em]">
                    {t("feedbackPage.stepLabel", { number: index + 1 })}
                  </p>
                  <p className="mt-1 text-sm font-medium leading-tight">
                    {step.title}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {submitted ? (
            <CardContent className="p-4 sm:p-8">
              <div className="flex flex-col items-center py-8 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <Badge className="mt-5 bg-primary text-primary-foreground">
                  {t("feedbackPage.success.badge")}
                </Badge>
                <h3 className="mt-4 font-ui text-2xl font-semibold text-foreground">
                  {t("feedbackPage.success.title")}
                </h3>
                <p className="mt-2 max-w-lg text-sm text-muted-foreground sm:text-base">
                  {t("feedbackPage.success.note")}
                </p>
                <div className="mt-8 flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(ROUTES.DASHBOARD)}
                    className="sm:min-w-44"
                  >
                    {t("feedbackPage.actions.dashboard")}
                  </Button>
                  <Button
                    type="button"
                    onClick={resetForm}
                    className="sm:min-w-52"
                  >
                    {t("feedbackPage.actions.restart")}
                  </Button>
                </div>
              </div>
            </CardContent>
          ) : (
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)}>
                <CardHeader className="px-4 pb-0 pt-4 sm:px-8 sm:pt-6">
                  <CardTitle className="font-ui text-xl">
                    {steps[currentStep].title}
                  </CardTitle>
                  <CardDescription>
                    {steps[currentStep].description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6 px-4 py-4 sm:space-y-8 sm:px-8 sm:py-6">
                  {currentStep === 0 && (
                    <>
                      <FormField
                        control={form.control}
                        name="overallRating"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("feedbackPage.fields.overallRating")}
                            </FormLabel>
                            <FormDescription>
                              {t(
                                "feedbackPage.fields.overallRatingDescription",
                              )}
                            </FormDescription>
                            <FormControl>
                              <div className="grid grid-cols-5 gap-1.5 sm:gap-3">
                                {[1, 2, 3, 4, 5].map((rating) => (
                                  <button
                                    key={rating}
                                    type="button"
                                    onClick={() => field.onChange(rating)}
                                    className={cn(
                                      "min-w-0 rounded-xl border px-1.5 py-3 text-center transition-colors sm:rounded-2xl sm:px-4 sm:py-4 sm:text-left",
                                      field.value === rating
                                        ? "border-primary bg-primary/10 text-foreground"
                                        : "border-border bg-background hover:border-primary/30 hover:bg-muted/30",
                                    )}
                                  >
                                    <span className="block text-xl font-semibold text-foreground sm:text-2xl">
                                      {rating}
                                    </span>
                                    <span className="mt-1 block break-words text-[10px] leading-tight text-muted-foreground sm:text-xs">
                                      {t(
                                        `feedbackPage.options.rating.${rating}`,
                                      )}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="usageFrequency"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("feedbackPage.fields.usageFrequency")}
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={t(
                                      "feedbackPage.fields.usageFrequencyPlaceholder",
                                    )}
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {USAGE_FREQUENCIES.map((option) => (
                                  <SelectItem key={option} value={option}>
                                    {t(
                                      `feedbackPage.options.usageFrequency.${option}`,
                                    )}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="goalSupportRating"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("feedbackPage.fields.goalSupportRating")}
                            </FormLabel>
                            <FormDescription>
                              {t(
                                "feedbackPage.fields.goalSupportRatingDescription",
                              )}
                            </FormDescription>
                            <FormControl>
                              <div className="grid grid-cols-5 gap-1.5 sm:gap-3">
                                {[1, 2, 3, 4, 5].map((rating) => (
                                  <button
                                    key={rating}
                                    type="button"
                                    onClick={() => field.onChange(rating)}
                                    className={cn(
                                      "min-w-0 rounded-xl border px-1.5 py-3 text-center transition-colors sm:rounded-2xl sm:px-4 sm:py-4 sm:text-left",
                                      field.value === rating
                                        ? "border-primary bg-primary/10 text-foreground"
                                        : "border-border bg-background hover:border-primary/30 hover:bg-muted/30",
                                    )}
                                  >
                                    <span className="block text-xl font-semibold text-foreground sm:text-2xl">
                                      {rating}
                                    </span>
                                    <span className="mt-1 block break-words text-[10px] leading-tight text-muted-foreground sm:text-xs">
                                      {t(
                                        `feedbackPage.options.rating.${rating}`,
                                      )}
                                    </span>
                                  </button>
                                ))}
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}

                  {currentStep === 1 && (
                    <>
                      <FormField
                        control={form.control}
                        name="usedAreas"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("feedbackPage.fields.usedAreas")}
                            </FormLabel>
                            <FormDescription>
                              {t("feedbackPage.fields.usedAreasDescription")}
                            </FormDescription>
                            <div className="grid gap-3 sm:grid-cols-2">
                              {FEEDBACK_AREAS.map((area) => {
                                const selectedValues = field.value ?? [];
                                const isSelected =
                                  selectedValues.includes(area);

                                return (
                                  <label
                                    key={area}
                                    className={cn(
                                      "flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-colors",
                                      isSelected
                                        ? "border-primary bg-primary/10"
                                        : "border-border bg-background hover:border-primary/30 hover:bg-muted/30",
                                    )}
                                  >
                                    <Checkbox
                                      checked={isSelected}
                                      onCheckedChange={(checked) => {
                                        if (checked === true && !isSelected) {
                                          field.onChange([
                                            ...selectedValues,
                                            area,
                                          ]);
                                          return;
                                        }

                                        if (checked !== true && isSelected) {
                                          field.onChange(
                                            selectedValues.filter(
                                              (value) => value !== area,
                                            ),
                                          );
                                        }
                                      }}
                                    />
                                    <span className="text-sm font-medium text-foreground">
                                      {t(`feedbackPage.options.areas.${area}`)}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="primaryArea"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("feedbackPage.fields.primaryArea")}
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={t(
                                      "feedbackPage.fields.primaryAreaPlaceholder",
                                    )}
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {FEEDBACK_AREAS.map((area) => (
                                  <SelectItem key={area} value={area}>
                                    {t(`feedbackPage.options.areas.${area}`)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="deviceContext"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("feedbackPage.fields.deviceContext")}
                            </FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={t(
                                      "feedbackPage.fields.deviceContextPlaceholder",
                                    )}
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {DEVICE_CONTEXTS.map((device) => (
                                  <SelectItem key={device} value={device}>
                                    {t(
                                      `feedbackPage.options.deviceContext.${device}`,
                                    )}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}

                  {currentStep === 2 && (
                    <>
                      <FormField
                        control={form.control}
                        name="likedMost"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("feedbackPage.fields.likedMost")}
                            </FormLabel>
                            <FormDescription>
                              {t("feedbackPage.fields.likedMostDescription")}
                            </FormDescription>
                            <FormControl>
                              <Textarea
                                {...field}
                                value={field.value ?? ""}
                                placeholder={t(
                                  "feedbackPage.fields.likedMostPlaceholder",
                                )}
                                className="min-h-32 rounded-2xl"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="improvementAreas"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("feedbackPage.fields.improvementAreas")}
                            </FormLabel>
                            <FormDescription>
                              {t(
                                "feedbackPage.fields.improvementAreasDescription",
                              )}
                            </FormDescription>
                            <div className="grid gap-3 sm:grid-cols-2">
                              {IMPROVEMENT_AREAS.map((area) => {
                                const selectedValues = field.value ?? [];
                                const isSelected =
                                  selectedValues.includes(area);

                                return (
                                  <label
                                    key={area}
                                    className={cn(
                                      "flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition-colors",
                                      isSelected
                                        ? "border-primary bg-primary/10"
                                        : "border-border bg-background hover:border-primary/30 hover:bg-muted/30",
                                    )}
                                  >
                                    <Checkbox
                                      checked={isSelected}
                                      onCheckedChange={(checked) => {
                                        if (checked === true && !isSelected) {
                                          field.onChange([
                                            ...selectedValues,
                                            area,
                                          ]);
                                          return;
                                        }

                                        if (checked !== true && isSelected) {
                                          field.onChange(
                                            selectedValues.filter(
                                              (value) => value !== area,
                                            ),
                                          );
                                        }
                                      }}
                                    />
                                    <span className="text-sm font-medium text-foreground">
                                      {t(
                                        `feedbackPage.options.improvementAreas.${area}`,
                                      )}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="experienceDetails"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("feedbackPage.fields.experienceDetails")}
                            </FormLabel>
                            <FormDescription>
                              {t(
                                "feedbackPage.fields.experienceDetailsDescription",
                              )}
                            </FormDescription>
                            <FormControl>
                              <Textarea
                                {...field}
                                value={field.value ?? ""}
                                placeholder={t(
                                  "feedbackPage.fields.experienceDetailsPlaceholder",
                                )}
                                className="min-h-36 rounded-2xl"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </>
                  )}

                  {currentStep === 3 && (
                    <>
                      <FormField
                        control={form.control}
                        name="suggestions"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t("feedbackPage.fields.suggestions")}
                            </FormLabel>
                            <FormDescription>
                              {t("feedbackPage.fields.suggestionsDescription")}
                            </FormDescription>
                            <FormControl>
                              <Textarea
                                {...field}
                                value={field.value ?? ""}
                                placeholder={t(
                                  "feedbackPage.fields.suggestionsPlaceholder",
                                )}
                                className="min-h-36 rounded-2xl"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="contactConsent"
                        render={({ field }) => (
                          <FormItem className="rounded-2xl border border-border/70 bg-muted/20 p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div className="space-y-1">
                                <FormLabel>
                                  {t("feedbackPage.fields.contactConsent")}
                                </FormLabel>
                                <FormDescription>
                                  {t(
                                    "feedbackPage.fields.contactConsentDescription",
                                  )}
                                </FormDescription>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {contactConsent && (
                        <FormField
                          control={form.control}
                          name="contactEmail"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {t("feedbackPage.fields.contactEmail")}
                              </FormLabel>
                              <FormDescription>
                                {t(
                                  "feedbackPage.fields.contactEmailDescription",
                                )}
                              </FormDescription>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value ?? ""}
                                  type="email"
                                  placeholder={t(
                                    "feedbackPage.fields.contactEmailPlaceholder",
                                  )}
                                  className="h-11 rounded-xl"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </>
                  )}
                </CardContent>

                <div className="safe-area-bottom flex flex-col-reverse gap-2 border-t border-border/60 px-4 pt-4 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-8 sm:py-5">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={
                      currentStep === 0
                        ? () => navigate(ROUTES.DASHBOARD)
                        : handleBack
                    }
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t("feedbackPage.actions.back")}
                  </Button>

                  {currentStep < steps.length - 1 ? (
                    <Button type="button" onClick={handleNext}>
                      {t("feedbackPage.actions.next")}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  ) : (
                    <Button type="submit" disabled={isSubmitting}>
                      {isSubmitting
                        ? t("feedbackPage.actions.submitting")
                        : t("feedbackPage.actions.submit")}
                    </Button>
                  )}
                </div>
              </form>
            </Form>
          )}
        </Card>
      </div>
    </StandardPageShell>
  );
}
